import { AxiosResponse } from "axios";
import { IValidationResult } from "lobbie";
import { useCallback, useState } from "react";
import { BASE_PUBLIC_API_ROUTE, DEFAULT_ERROR_MESSAGE, LOBBIE_STORAGE } from "src/constants";
import { useAxiosPublicPost } from "src/hooks/useAxios";
import { useLogoutNoRedirect } from "src/hooks/user";
import { handleError, isFailedRequest, localSet, logDev, notify } from "src/utils";

export const useAnonmyousPatientV2 = (
    accountPublicUuid: string | undefined,
    {
        formTemplateGroupPublicUuid,
        formTemplatePublicUuid,
        locationId,
    }: {
        formTemplateGroupPublicUuid?: string;
        formTemplatePublicUuid?: string;
        locationId?: string;
    },
) => {
    const poster = useAxiosPublicPost();
    const logout = useLogoutNoRedirect(true);
    const [isLoading, setLoading] = useState<boolean>(false);

    const create = useCallback(
        async (firstName: string, lastName: string, dateOfBirth?: string) => {
            if (!formTemplateGroupPublicUuid && !formTemplatePublicUuid) {
                return;
            }

            setLoading(true);

            await logout().catch(console.error);

            const route = `${BASE_PUBLIC_API_ROUTE}/forms/groups/anonymous/patient/v2`;
            const body = {
                firstName,
                lastName,
                dateOfBirth,
                formTemplateGroupPublicUuid,
                formTemplatePublicUuid,
                accountPublicUuid,
                locationId,
            };

            return poster(route, body)
                .then((response: AxiosResponse | void) => {
                    const result =
                        response && (response.data as { qrLink: string } | IValidationResult);
                    logDev("useAnonmyousPatient.create RESPONSE DATA -", result);
                    if (!result) {
                        setLoading(false);
                    } else if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: "Failed to initialize.",
                            message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                        });
                        setLoading(false);
                    } else {
                        const link = (result as { qrLink: string }).qrLink;
                        if (link) {
                            const url = new URL(decodeURIComponent(link));
                            const token =
                                url.searchParams.get("actionId") || url.searchParams.get("token");

                            if (token) {
                                localSet(`logout/${token}`, "1");
                            }

                            localSet(
                                LOBBIE_STORAGE.Session.Patients.Forms.Anonymous.AccountPublicUuid,
                                accountPublicUuid,
                            );
                            localSet(
                                LOBBIE_STORAGE.Session.Patients.Forms.Anonymous.LocationId,
                                locationId,
                            );
                            localSet(
                                LOBBIE_STORAGE.Session.Patients.Forms.Anonymous
                                    .FormTemplatePublicUuid,
                                formTemplatePublicUuid,
                            );
                            localSet(
                                LOBBIE_STORAGE.Session.Patients.Forms.Anonymous
                                    .FormTemplateGroupPublicUuid,
                                formTemplateGroupPublicUuid,
                            );

                            window.location.href = url.href;
                        } else {
                            setLoading(false);
                        }
                    }
                })
                .catch((error) => {
                    setLoading(false);
                    handleError(error);
                });
        },
        [
            accountPublicUuid,
            poster,
            formTemplatePublicUuid,
            formTemplateGroupPublicUuid,
            locationId,
            logout,
        ],
    );

    return {
        create,
        isLoading,
    };
};
